import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AuroraHeader, AuroraFooter } from "../components/aurora/Header";
import { MultiSelect, SingleSelect } from "../components/aurora/MultiSelect";
import {
  COUNTRIES,
  INDUSTRIES,
  ROLES,
  COMPANY_SIZES,
  REVENUE_RANGES,
  REGULATORY,
  ACTIVE_CONCERNS,
  PLANNING_HORIZONS,
  ADMIN_STATUSES,
  ADMIN_STATUS_COLORS,
} from "../lib/countries";
import { api } from "../lib/api";
import InlineResultEditor from "../components/aurora/InlineResultEditor";
import { ArrowLeft, Copy, Check, FileText, Star } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mb-12">
    <h3 className="font-heading text-xl text-[#A88059] mb-6 tracking-wide">{title}</h3>
    <div className="space-y-6">{children}</div>
  </section>
);

const Row = ({ label, children }) => (
  <div>
    <div className="label-eyebrow mb-2">{label}</div>
    {children}
  </div>
);

const APP_BASE = process.env.REACT_APP_BACKEND_URL;

const generateButtonLabel = (generating, hasToken) => {
  if (generating) return "Generating Assessment...";
  if (hasToken) return "Regenerate Assessment";
  return "Generate Full Assessment";
};

export default function AdminSubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .adminGet(id)
      .then((r) => {
        if (!cancelled) setSub(r.data);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 401) {
          navigate("/admin/login");
        } else {
          toast.error("Submission not found");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const update = (section, patch) =>
    setSub((s) => ({ ...s, [section]: { ...s[section], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.adminUpdate(id, {
        companyProfile: sub.companyProfile,
        operationalExposure: sub.operationalExposure,
        currentConcerns: sub.currentConcerns,
        strategicContext: sub.strategicContext,
      });
      toast.success("Changes saved");
      const r = await api.adminGet(id);
      setSub(r.data);
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      // Save latest first
      await api.adminUpdate(id, {
        companyProfile: sub.companyProfile,
        operationalExposure: sub.operationalExposure,
        currentConcerns: sub.currentConcerns,
        strategicContext: sub.strategicContext,
      });
      // Kick off the background generation
      await api.adminGenerate(id);
      // Poll every 3s until status flips to Assessment Ready or Generation Failed
      const start = Date.now();
      const poll = async () => {
        try {
          const fresh = await api.adminGet(id);
          if (fresh.data.status === "Assessment Ready" && fresh.data.fullResult) {
            setSub(fresh.data);
            setGenerating(false);
            toast.success("Full assessment generated");
            return;
          }
          if (fresh.data.status === "Generation Failed") {
            setSub(fresh.data);
            setGenerating(false);
            toast.error(fresh.data.fullGenerationError || "Generation failed");
            return;
          }
          if (Date.now() - start > 5 * 60 * 1000) {
            setGenerating(false);
            toast.error("Generation timed out — please retry");
            return;
          }
          setTimeout(poll, 3000);
        } catch (err) {
          setGenerating(false);
          toast.error("Lost connection while generating");
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start generation");
      setGenerating(false);
    }
  };

  const resultUrl = sub?.resultToken ? `${APP_BASE}/results/${sub.resultToken}` : "";
  const copy = () => {
    navigator.clipboard.writeText(resultUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Link copied");
  };

  const downloadDocx = async () => {
    setDownloadingDocx(true);
    try {
      const res = await api.adminDownloadDocx(id);
      // Extract filename from Content-Disposition when present, otherwise fall back
      const disposition = res.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const filename = match?.[1] || `Aurora_Assessment_${sub?.companyProfile?.companyName || "client"}.docx`;
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("DOCX downloaded");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to download DOCX";
      toast.error(typeof detail === "string" ? detail : "Failed to download DOCX");
    } finally {
      setDownloadingDocx(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <AuroraHeader compact />
        <div className="flex-1 flex items-center justify-center label-eyebrow text-[#6B4D2B]">
          Loading submission...
        </div>
      </div>
    );
  }

  if (!sub) {
    return (
      <div className="min-h-screen flex flex-col">
        <AuroraHeader compact />
        <div className="flex-1 flex items-center justify-center font-body text-[#6B5E4A]">
          Not found.
        </div>
        <AuroraFooter />
      </div>
    );
  }

  const cp = sub.companyProfile || {};
  const op = sub.operationalExposure || {};
  const cc = sub.currentConcerns || {};
  const sc = sub.strategicContext || {};

  return (
    <div className="min-h-screen flex flex-col">
      <AuroraHeader compact />
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 sm:px-10 py-12">
        <button
          onClick={() => navigate("/admin")}
          className="btn-ghost mb-8 inline-flex items-center"
          data-testid="btn-back-admin"
        >
          <ArrowLeft size={14} className="mr-2" /> Back to Dashboard
        </button>

        <div className="label-eyebrow text-[#6B4D2B] mb-3">Submission Detail</div>
        <h2 className="font-heading-bold text-4xl text-[#190F08] mb-2">
          {cp.companyName || "Untitled"}
        </h2>
        <div className="font-body text-[#6B5E4A] text-sm mb-2">
          ID: {sub.submissionId} · Submitted{" "}
          {sub.createdAt ? new Date(sub.createdAt).toLocaleString("en-GB") : "—"}
        </div>
        <div
          className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-6 gap-y-2 mb-8 border-l-2 border-[#A88059] pl-4 py-2"
          data-testid="submission-header-info"
        >
          <div className="label-eyebrow text-[#6B4D2B]">Client Email</div>
          <div
            className="font-body text-[#190F08] text-base break-all"
            data-testid="submission-client-email"
          >
            {sub.clientEmail ? (
              <a
                className="text-[#6B4D2B] underline decoration-[#A88059]/40 hover:text-[#190F08] hover:decoration-[#190F08] transition-colors"
                href={`mailto:${sub.clientEmail}`}
              >
                {sub.clientEmail}
              </a>
            ) : (
              <span className="italic text-[#6B5E4A]">Not provided</span>
            )}
          </div>
          <div className="label-eyebrow text-[#6B4D2B]">Status</div>
          <div className="font-body text-[#190F08] text-base flex items-center gap-3 flex-wrap">
            <span>{sub.status}</span>
            {sub.edited && (
              <span
                className="inline-block px-2 py-0.5 text-[10px] font-heading uppercase tracking-[0.22em] border border-[#D49A36] text-[#D49A36]"
                data-testid="edited-badge-header"
              >
                Edited
              </span>
            )}
          </div>
          <div className="label-eyebrow text-[#6B4D2B]">Workflow</div>
          <div className="font-body text-[#190F08] text-base flex items-center gap-3 flex-wrap">
            <select
              value={sub.adminStatus || "New"}
              onChange={async (e) => {
                const next = e.target.value;
                try {
                  await api.adminSetAdminStatus(id, next);
                  setSub((s) => ({ ...s, adminStatus: next }));
                  toast.success(`Workflow → ${next}`);
                } catch (err) {
                  toast.error("Failed to update workflow status");
                }
              }}
              className="aurora-select-inline"
              style={{
                borderColor:
                  ADMIN_STATUS_COLORS[sub.adminStatus || "New"] || "#A88059",
                color: ADMIN_STATUS_COLORS[sub.adminStatus || "New"] || "#6B5E4A",
              }}
              data-testid="select-admin-status"
            >
              {ADMIN_STATUSES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={async () => {
                const next = !sub.starred;
                try {
                  await api.adminSetStar(id, next);
                  setSub((s) => ({ ...s, starred: next }));
                  toast.success(next ? "Starred" : "Unstarred");
                } catch (err) {
                  toast.error("Failed to update star");
                }
              }}
              className="p-1 -m-1 hover:opacity-80 transition-opacity"
              aria-label={sub.starred ? "Unstar submission" : "Star submission"}
              data-testid="btn-star-detail"
            >
              <Star
                size={18}
                className={sub.starred ? "text-[#D49A36]" : "text-[#A88059]/50"}
                fill={sub.starred ? "#D49A36" : "none"}
              />
            </button>
          </div>
        </div>

        <hr className="gold-rule mb-10" />

        {/* Editable form */}
        <Section title="Company Profile">
          <Row label="Company Name">
            <input
              className="aurora-input"
              value={cp.companyName || ""}
              onChange={(e) => update("companyProfile", { companyName: e.target.value })}
              data-testid="edit-company-name"
            />
          </Row>
          <Row label="Role / Function">
            <SingleSelect
              testId="edit-role"
              options={ROLES}
              value={cp.role || ""}
              onChange={(v) => update("companyProfile", { role: v })}
            />
            {cp.role === "Other" && (
              <input
                className="aurora-input mt-3"
                placeholder="Specified role"
                value={cp.roleOther || ""}
                onChange={(e) => update("companyProfile", { roleOther: e.target.value })}
                data-testid="edit-role-other"
              />
            )}
          </Row>
          <Row label="Website">
            <input
              className="aurora-input"
              value={cp.companyWebsite || ""}
              onChange={(e) => update("companyProfile", { companyWebsite: e.target.value })}
            />
          </Row>
          <div className="grid md:grid-cols-2 gap-6">
            <Row label="Industry">
              <SingleSelect
                testId="edit-industry"
                options={INDUSTRIES}
                value={cp.industry || ""}
                onChange={(v) => update("companyProfile", { industry: v })}
              />
            </Row>
            <Row label="Headquarters">
              <SingleSelect
                testId="edit-hq"
                options={COUNTRIES}
                value={cp.headquartersCountry || ""}
                onChange={(v) => update("companyProfile", { headquartersCountry: v })}
              />
            </Row>
          </div>
          <Row label="Primary Markets">
            <MultiSelect
              testId="edit-markets"
              options={COUNTRIES}
              selected={cp.primaryMarkets || []}
              onChange={(v) => update("companyProfile", { primaryMarkets: v })}
            />
          </Row>
          <div className="grid md:grid-cols-2 gap-6">
            <Row label="Number of Employees">
              <SingleSelect
                testId="edit-size"
                options={COMPANY_SIZES}
                value={cp.companySize || ""}
                onChange={(v) => update("companyProfile", { companySize: v })}
              />
            </Row>
            <Row label="Annual Revenue">
              <SingleSelect
                testId="edit-revenue"
                options={REVENUE_RANGES}
                value={cp.annualRevenue || ""}
                onChange={(v) => update("companyProfile", { annualRevenue: v })}
              />
            </Row>
          </div>
        </Section>

        <Section title="Operational Exposure">
          <Row label="Supplier Locations">
            <MultiSelect
              testId="edit-suppliers"
              options={COUNTRIES}
              selected={op.supplierLocations || []}
              onChange={(v) => update("operationalExposure", { supplierLocations: v })}
            />
          </Row>
          <Row label="Asset Locations">
            <MultiSelect
              testId="edit-assets"
              options={COUNTRIES}
              selected={op.assetLocations || []}
              onChange={(v) => update("operationalExposure", { assetLocations: v })}
            />
          </Row>
          <Row label="Tier-2/3 Supplier Visibility">
            <div className="flex gap-6 flex-wrap">
              {["Yes", "No", "Not sure"].map((v) => (
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    className="aurora-check"
                    checked={op.tier23Visibility === v}
                    onChange={() => update("operationalExposure", { tier23Visibility: v })}
                    data-testid={`edit-tier23-${v.toLowerCase().replace(" ", "-")}`}
                  />
                  <span className="text-[#190F08]">{v}</span>
                </label>
              ))}
            </div>
          </Row>
          <Row label="Sourcing Visibility">
            <div className="flex gap-6 flex-wrap">
              {["Yes", "No", "Not sure"].map((v) => (
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    className="aurora-check"
                    checked={op.sourcingVisibility === v}
                    onChange={() => update("operationalExposure", { sourcingVisibility: v })}
                    data-testid={`edit-sourcing-${v.toLowerCase().replace(" ", "-")}`}
                  />
                  <span className="text-[#190F08]">{v}</span>
                </label>
              ))}
            </div>
          </Row>
          <Row label="Single-country dependency >30%">
            <div className="flex gap-6">
              {["Yes", "No"].map((v) => (
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    className="aurora-check"
                    checked={op.singleCountryDependency === v}
                    onChange={() => update("operationalExposure", { singleCountryDependency: v })}
                  />
                  <span className="text-[#190F08]">{v}</span>
                </label>
              ))}
            </div>
            {op.singleCountryDependency === "Yes" && (
              <div className="mt-3">
                <SingleSelect
                  testId="edit-single-dep-country"
                  options={COUNTRIES}
                  value={op.singleCountryDependencyCountry || ""}
                  onChange={(v) =>
                    update("operationalExposure", { singleCountryDependencyCountry: v })
                  }
                />
              </div>
            )}
          </Row>
          <Row label="Operations in Conflict Areas">
            <div className="flex gap-6">
              {["Yes", "No"].map((v) => (
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    className="aurora-check"
                    checked={op.conflictAreaOps === v}
                    onChange={() => update("operationalExposure", { conflictAreaOps: v })}
                  />
                  <span className="text-[#190F08]">{v}</span>
                </label>
              ))}
            </div>
            {op.conflictAreaOps === "Yes" && (
              <div className="mt-3">
                <MultiSelect
                  testId="edit-conflict-countries"
                  options={COUNTRIES}
                  selected={op.conflictAreaCountries || []}
                  onChange={(v) => update("operationalExposure", { conflictAreaCountries: v })}
                />
              </div>
            )}
          </Row>
          <Row label="Regulatory Jurisdictions">
            <MultiSelect
              testId="edit-regulatory"
              options={REGULATORY}
              selected={op.regulatoryJurisdictions || []}
              onChange={(v) => update("operationalExposure", { regulatoryJurisdictions: v })}
            />
          </Row>
          <Row label="Government/SOE Counterparty">
            <div className="flex gap-6 flex-wrap">
              {["Yes", "No", "Not sure"].map((v) => (
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    className="aurora-check"
                    checked={op.govSoeCounterparty === v}
                    onChange={() =>
                      update("operationalExposure", {
                        govSoeCounterparty: v,
                        govSoeCountries: v === "Yes" ? op.govSoeCountries || [] : [],
                      })
                    }
                    data-testid={`edit-gov-soe-${v.toLowerCase().replace(" ", "-")}`}
                  />
                  <span className="text-[#190F08]">{v}</span>
                </label>
              ))}
            </div>
            {op.govSoeCounterparty === "Yes" && (
              <div className="mt-3">
                <MultiSelect
                  testId="edit-gov-soe-countries"
                  options={COUNTRIES}
                  selected={op.govSoeCountries || []}
                  onChange={(v) => update("operationalExposure", { govSoeCountries: v })}
                />
              </div>
            )}
          </Row>
        </Section>

        <Section title="Current Concerns">
          <Row label="Active Concerns">
            <div className="grid sm:grid-cols-2 gap-3">
              {ACTIVE_CONCERNS.map((c) => {
                const sel = (cc.activeConcerns || []).includes(c);
                return (
                  <label key={c} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="aurora-check mt-1"
                      checked={sel}
                      onChange={() => {
                        const list = sel
                          ? cc.activeConcerns.filter((x) => x !== c)
                          : [...(cc.activeConcerns || []), c];
                        update("currentConcerns", { activeConcerns: list });
                      }}
                    />
                    <span className="text-[#190F08] text-sm">{c}</span>
                  </label>
                );
              })}
            </div>
            {(cc.activeConcerns || []).includes("Other") && (
              <input
                className="aurora-input mt-3"
                placeholder="Describe other"
                value={cc.activeConcernsOther || ""}
                onChange={(e) => update("currentConcerns", { activeConcernsOther: e.target.value })}
              />
            )}
          </Row>
          <Row label="Disruption Experienced (12 months)">
            <div className="flex gap-6">
              {["Yes", "No"].map((v) => (
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    className="aurora-check"
                    checked={cc.disruptionExperienced === v}
                    onChange={() => update("currentConcerns", { disruptionExperienced: v })}
                  />
                  <span className="text-[#190F08]">{v}</span>
                </label>
              ))}
            </div>
            <textarea
              className="aurora-input mt-3"
              value={cc.disruptionDescription || ""}
              onChange={(e) => update("currentConcerns", { disruptionDescription: e.target.value })}
            />
          </Row>
          <Row label="Contingency Plan">
            <div className="flex gap-6">
              {["Yes", "No"].map((v) => (
                <label key={v} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    className="aurora-check"
                    checked={cc.contingencyPlan === v}
                    onChange={() => update("currentConcerns", { contingencyPlan: v })}
                  />
                  <span className="text-[#190F08]">{v}</span>
                </label>
              ))}
            </div>
            <textarea
              className="aurora-input mt-3"
              value={cc.contingencyDescription || ""}
              onChange={(e) => update("currentConcerns", { contingencyDescription: e.target.value })}
            />
          </Row>
          <Row label="Planning Horizon">
            <SingleSelect
              testId="edit-horizon"
              options={PLANNING_HORIZONS}
              value={cc.planningHorizon || ""}
              onChange={(v) => update("currentConcerns", { planningHorizon: v })}
            />
          </Row>
          <Row label="Top Priority Risks">
            {(cc.topPriorityRisks || []).length === 0 ? (
              <div className="font-body text-sm text-[#6B5E4A] italic">
                None selected by client
              </div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="admin-top-priority-risks">
                {(cc.topPriorityRisks || []).map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-2 px-3 py-1 border border-[#A88059] bg-[#A88059]/10"
                    data-testid={`top-priority-badge-${p.slice(0, 20)}`}
                  >
                    <span
                      className="text-[10px] font-heading uppercase tracking-[0.18em] text-[#A88059]"
                    >
                      Top Priority
                    </span>
                    <span className="font-body text-sm text-[#190F08]">{p}</span>
                  </span>
                ))}
              </div>
            )}
          </Row>
          <Row label="Specific country / market of concern (optional)">
            <SingleSelect
              testId="edit-concern-country"
              options={COUNTRIES}
              value={cc.concernCountry || ""}
              onChange={(v) => update("currentConcerns", { concernCountry: v })}
            />
            {cc.concernCountry && (
              <input
                className="aurora-input mt-3"
                placeholder="Client's note (max 150 chars)"
                maxLength={150}
                value={cc.concernCountryNote || ""}
                onChange={(e) =>
                  update("currentConcerns", { concernCountryNote: e.target.value.slice(0, 150) })
                }
                data-testid="edit-concern-country-note"
              />
            )}
          </Row>
        </Section>

        <Section title="Strategic Context">
          <Row label="Decision Informing">
            <textarea
              className="aurora-input"
              value={sc.decisionInforming || ""}
              onChange={(e) => update("strategicContext", { decisionInforming: e.target.value })}
            />
          </Row>
          <Row label="Additional Context">
            <textarea
              className="aurora-input"
              value={sc.additionalContext || ""}
              onChange={(e) => update("strategicContext", { additionalContext: e.target.value })}
            />
          </Row>
        </Section>

        {/* Actions */}
        <div className="border border-[#A88059] p-8 mb-12" data-testid="generate-section">
          <div className="label-eyebrow text-[#6B4D2B] mb-3">AI Generation</div>
          <h3 className="font-heading text-xl text-[#190F08] mb-3">Full Assessment</h3>
          <p className="font-body text-[#6B5E4A] text-sm mb-6">
            Saves your edits and runs the full Claude Sonnet 4 assessment. This takes 30–60 seconds.
          </p>

          {sub.resultToken && (
            <div className="mb-6 border border-[#A88059]/40 p-4 bg-[#A88059]/5">
              <div className="label-eyebrow mb-2 text-[#6B4D2B]">Client Result Link</div>
              <div className="flex items-center gap-3 flex-wrap">
                <a
                  href={resultUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-base text-[#A88059] underline decoration-[#A88059]/40 hover:text-[#190F08] hover:decoration-[#190F08] break-all transition-colors"
                  data-testid="result-url"
                >
                  {resultUrl}
                </a>
                <button onClick={copy} className="btn-ghost" data-testid="btn-copy-link">
                  {copied ? <Check size={14} className="inline mr-1" /> : <Copy size={14} className="inline mr-1" />}
                  {copied ? "Copied" : "Copy"}
                </button>
                <a
                  href={resultUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost"
                  data-testid="btn-open-result"
                >
                  Open ↗
                </a>
              </div>
            </div>
          )}

          <div className="flex gap-4 flex-wrap">
            <button
              onClick={save}
              disabled={saving || generating}
              className="btn-ghost"
              data-testid="btn-save-edits"
            >
              {saving ? "Saving..." : "Save Edits"}
            </button>
            <button
              onClick={generate}
              disabled={generating}
              className="btn-primary"
              data-testid="btn-generate"
            >
              {generateButtonLabel(generating, sub.resultToken)}
            </button>
            {sub.fullResult && (
              <button
                onClick={downloadDocx}
                disabled={downloadingDocx}
                className="btn-ghost inline-flex items-center"
                data-testid="btn-download-docx"
              >
                <FileText size={14} className="inline mr-2" />
                {downloadingDocx ? "Preparing DOCX..." : "Download DOCX"}
              </button>
            )}
          </div>

          {generating && (
            <div className="mt-6 label-eyebrow text-[#6B4D2B]" data-testid="generating-loader">
              Claude is composing the brief · please wait
            </div>
          )}
        </div>

        {sub.fullResult && (
          <InlineResultEditor
            sub={sub}
            resultUrl={resultUrl}
            onChange={async () => {
              const r = await api.adminGet(id);
              setSub(r.data);
            }}
          />
        )}
      </main>
      <AuroraFooter />
    </div>
  );
}
